// ===============================================================
// AI KULLANIMI LOGLAMA VE DENETİM SERVİSİ (Firestore students & admins Senkronizasyonu)
// ===============================================================

const GLOBAL_AI_LOGS_KEY = 'kocum_ai_global_logs';

/**
 * Öğrencinin yazdığı promptun amacını (intent) tespit eder
 */
export function detectAiIntent(text = '') {
  const t = (text || '').toLowerCase();
  if (t.includes('program') || t.includes('koçluk') || t.includes('planla') || t.includes('takvim') || t.includes('çalışma planı')) {
    return 'Program/Koçluk Talebi (Engellendi)';
  }
  if (t.includes('kaç soru') || t.includes('deneme') || t.includes('net') || t.includes('istatistik') || t.includes('durum')) {
    return 'İstatistik & Veri';
  }
  if (t.includes('kazanım') || t.includes('konu') || t.includes('ünite') || t.includes('hangi ders') || t.includes('hangi konu')) {
    return 'Kazanım & Konu Tespiti';
  }
  if (t.includes('çöz') || t.includes('soru') || t.includes('formül') || t.includes('bul') || t.includes('ispat') || t.includes('nedir')) {
    return 'Soru Çözümü';
  }
  return 'Genel Soru / Rehberlik';
}

/**
 * Bir öğrencinin AI etkileşimini yerel depolamaya ve Firestore'daki öğrenci dokümanına kaydeder
 */
export async function saveStudentAiInteraction({
  student,
  userPrompt,
  aiResponse,
  hasImage = false,
  imagePreview = null,
  db = null,
  isFirebaseActive = false
}) {
  if (!student) return;

  const now = new Date();
  const intent = detectAiIntent(userPrompt);

  const logEntry = {
    id: `log_${now.getTime()}_${Math.random().toString(36).substr(2, 6)}`,
    studentId: String(student.id),
    studentName: student.name || 'Öğrenci',
    studentClass: student.grade || student.class || 'Genel',
    teacherId: student.teacherId || '',
    userPrompt: userPrompt || '(Görsel Gönderildi)',
    aiResponse: aiResponse || '',
    hasImage: !!hasImage,
    imagePreview: imagePreview ? imagePreview.slice(0, 300) : null,
    intent: intent,
    date: now.toLocaleDateString('tr-TR'),
    time: now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    timestamp: now.toISOString()
  };

  // 1. LocalStorage Kaydı
  try {
    const raw = localStorage.getItem(GLOBAL_AI_LOGS_KEY);
    const logs = raw ? JSON.parse(raw) : [];
    logs.unshift(logEntry);
    if (logs.length > 500) logs.pop();
    localStorage.setItem(GLOBAL_AI_LOGS_KEY, JSON.stringify(logs));
  } catch (e) {
    console.warn("AI Log localStorage kaydetme hatası:", e);
  }

  // 2. Firebase Kaydı (Doğrudan öğrenci dokümanına anlık yazar - Tüm tarayıcılarda anında güncellenir)
  try {
    const { doc, updateDoc, arrayUnion, increment, getFirestore, addDoc, collection } = await import("firebase/firestore");
    let firestoreDb = db;
    if (!firestoreDb) {
      const { getApp } = await import("firebase/app");
      firestoreDb = getFirestore(getApp());
    }

    if (firestoreDb && student.id) {
      // A. Öğrenci dokümanını güncelle (Firestore onSnapshot bunu anında süperadmine iter)
      const studentRef = doc(firestoreDb, "students", String(student.id));
      await updateDoc(studentRef, {
        aiPromptCount: increment(1),
        aiInteractions: arrayUnion(logEntry),
        lastAiPrompt: logEntry.userPrompt,
        lastAiTime: now.toISOString(),
        lastAiIntent: intent
      });

      // B. notifications koleksiyonuna da AI log kaydı at (Tam log havuzu için)
      try {
        await addDoc(collection(firestoreDb, "notifications"), {
          isAiLogNotification: true,
          ...logEntry,
          createdAt: now.toISOString()
        });
      } catch (ne) {}
    }
  } catch (fbErr) {
    console.warn("AI Log Firestore kaydetme hatası:", fbErr);
  }

  return logEntry;
}

/**
 * Öğretmenin AI Eğitim Danışmanı ile yaptığı etkileşimi kaydeder
 */
export async function saveTeacherAiInteraction({
  teacher,
  userPrompt,
  aiResponse,
  db = null,
  isFirebaseActive = false
}) {
  if (!teacher) return;

  const now = new Date();
  const intent = 'Koçluk & Raporlama (Öğretmen)';

  const logEntry = {
    id: `log_tr_${now.getTime()}_${Math.random().toString(36).substr(2, 6)}`,
    studentId: String(teacher.id),
    studentName: teacher.name || 'Öğretmen',
    userRole: 'teacher',
    studentClass: 'Öğretmen / Koç',
    teacherId: teacher.id,
    userPrompt: userPrompt || '',
    aiResponse: aiResponse || '',
    hasImage: false,
    imagePreview: null,
    intent: intent,
    date: now.toLocaleDateString('tr-TR'),
    time: now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    timestamp: now.toISOString()
  };

  try {
    const raw = localStorage.getItem(GLOBAL_AI_LOGS_KEY);
    const logs = raw ? JSON.parse(raw) : [];
    logs.unshift(logEntry);
    if (logs.length > 500) logs.pop();
    localStorage.setItem(GLOBAL_AI_LOGS_KEY, JSON.stringify(logs));
  } catch (e) {
    console.warn("Öğretmen AI log localStorage hatası:", e);
  }

  try {
    const { doc, updateDoc, arrayUnion, increment, getFirestore, addDoc, collection } = await import("firebase/firestore");
    let firestoreDb = db;
    if (!firestoreDb) {
      const { getApp } = await import("firebase/app");
      firestoreDb = getFirestore(getApp());
    }

    if (firestoreDb && teacher.id) {
      const teacherRef = doc(firestoreDb, "admins", String(teacher.id));
      await updateDoc(teacherRef, {
        aiPromptCount: increment(1),
        aiInteractions: arrayUnion(logEntry),
        lastAiPrompt: logEntry.userPrompt,
        lastAiTime: now.toISOString(),
        lastAiIntent: intent
      });

      try {
        await addDoc(collection(firestoreDb, "notifications"), {
          isAiLogNotification: true,
          ...logEntry,
          createdAt: now.toISOString()
        });
      } catch (ne) {}
    }
  } catch (fbErr) {
    console.warn("Öğretmen AI log Firestore hatası:", fbErr);
  }

  return logEntry;
}

/**
 * Kayıtlı tüm AI loglarını getirir
 */
export function getLocalAiLogs() {
  try {
    const raw = localStorage.getItem(GLOBAL_AI_LOGS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}
